import type { Reward } from '@/models/supabase/Reward'

/**
 * Group household rewards by child, reproducing the `get_rewards_for_child` RPC:
 * a child sees a reward if it is explicitly assigned to them, OR if the reward
 * has NO child assignments at all (assigned to everyone by default).
 *
 * Mirrors the SQL in supabase/migrations/.../get_rewards_for_child: the only
 * filter is the assignment rule above (the RPC adds no expiration/availability
 * filtering — it just ORDERs by required_points then created_at, and consumers
 * re-sort themselves, so ordering is not reproduced here).
 *
 * @param householdRewards all rewards in the household
 * @param assignments rows from child_rewards: { reward_id, child_id }
 * @param childIds the children to build the map for
 * @returns Map<childId, Reward[]>
 */
export function groupRewardsByChild(
  householdRewards: Reward[],
  assignments: Array<{ reward_id: string; child_id: string }>,
  childIds: string[],
): Map<string, Reward[]> {
  // reward_id -> Set<child_id> of explicit assignments
  const assigned = new Map<string, Set<string>>()
  for (const a of assignments) {
    if (!assigned.has(a.reward_id)) assigned.set(a.reward_id, new Set())
    assigned.get(a.reward_id)!.add(a.child_id)
  }
  const out = new Map<string, Reward[]>()
  for (const childId of childIds) {
    out.set(
      childId,
      householdRewards.filter((reward) => {
        const set = assigned.get(reward.id)
        // No assignments for this reward → visible to all; else only if assigned to this child.
        return !set || set.size === 0 ? true : set.has(childId)
      }),
    )
  }
  return out
}
